import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import React, { useState } from 'react';

import { IUseFormData } from '../../common/contracts';
import {
  inputLabelProps,
  inputMUIDefaultStyles,
} from '../../common/styles';
import { validateEqualPasswords } from '../../common/validations/custom';
import { passwordValidation } from '../../common/validations/forms';

interface PasswordInputProps {
  title: string;
  helperText?: string;
  formData: IUseFormData;
  propertieFormName: string;
  className?: string;
  style?: React.CSSProperties;
}

export function PasswordInput({
  title,
  helperText,
  formData,
  propertieFormName,
  className,
}: PasswordInputProps): JSX.Element {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    errors,
    watch,
  } = formData;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down('xs'),
  );

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const getFieldValidation = () => {
    if (propertieFormName === 'confirmPassword') {
      return {
        ...passwordValidation,
        validate: {
          ...passwordValidation.validate,
          equalPasswords: (value: string) =>
            validateEqualPasswords(value, watch()),
        },
      };
    }

    if (propertieFormName === 'password') {
      return {
        ...passwordValidation,
        deps: ['confirmPassword'],
      };
    }

    return passwordValidation;
  };

  return (
    <FormControl
      variant="outlined"
      required
      className={className}
      style={{
        ...inputMUIDefaultStyles[
          isSmallScreen ? 'normal' : 'large'
        ],
        marginBottom: '40px',
      }}
    >
      <InputLabel
        htmlFor={`password-${propertieFormName}`}
        style={inputLabelProps.style}
      >
        {title}
      </InputLabel>

      <OutlinedInput
        id={`password-${propertieFormName}`}
        type={showPassword ? 'text' : 'password'}
        error={Boolean(errors?.[propertieFormName])}
        {...register(
          propertieFormName,
          getFieldValidation(),
        )}
        inputProps={{
          maxLength: 128,
        }}
        endAdornment={(
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showPassword
                ? <VisibilityOff />
                : <Visibility />}
            </IconButton>
          </InputAdornment>
        )}
      />

      <FormHelperText
        id={`${propertieFormName}-helper-text`}
        error={Boolean(errors?.[propertieFormName])}
      >
        {
          errors?.[propertieFormName]
            ? errors[propertieFormName]?.message
            : helperText ?? ' '
        }
      </FormHelperText>
    </FormControl>
  );
}
